import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { FlexColumn } from 'src/components/flex';
import type { IProperty } from 'src/services/api';
import { useUpdatePropertyApplicationLinks } from 'src/services/api';
import styles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';
import { useNotifications } from 'src/notifications';

export interface PropertyApplicationLinksSectionRef {
    applyChanges: () => Promise<boolean>;
}

interface Props {
    property: IProperty;
}

const PropertyApplicationLinksSection = forwardRef<PropertyApplicationLinksSectionRef, Props>(
    function PropertyApplicationLinksSection({ property }, ref) {
        const { mutateAsync: updatePropertyApplicationLinks } = useUpdatePropertyApplicationLinks();
        const { addNotification } = useNotifications();
        const initialLink = property.application_link?.link || '';
        const [link, setLink] = useState(initialLink);
        const [savedLink, setSavedLink] = useState(initialLink);
        const [saveError, setSaveError] = useState<string | null>(null);
        const [isSaving, setIsSaving] = useState(false);

        useEffect(() => {
            setLink(initialLink);
            setSavedLink(initialLink);
            setSaveError(null);
        }, [initialLink]);

        const applyChanges = useCallback(async () => {
            setSaveError(null);

            if (link === savedLink) {
                addNotification('Application link is already up to date', 'success');
                return true;
            }

            setIsSaving(true);
            try {
                await updatePropertyApplicationLinks([{ id: property.id, link }]);
                setSavedLink(link);
                return true;
            } catch {
                setSaveError('Failed to save the application link. Property was not updated.');
                return false;
            } finally {
                setIsSaving(false);
            }
        }, [addNotification, link, property.id, savedLink, updatePropertyApplicationLinks]);

        useImperativeHandle(ref, () => ({ applyChanges }), [applyChanges]);

        return (
            <FlexColumn rowGap={2}>
                <Typography fontWeight={500} data-property-section-title>Application Settings</Typography>

                {saveError && (
                    <Alert severity="error" data-testid="application-links-error-alert">
                        {saveError}
                    </Alert>
                )}

                <div className={styles.linkSaveRow}>
                    <TextField
                        label="Application Link"
                        placeholder="https://calendly.com/name/application"
                        value={link}
                        onChange={(event) => setLink(event.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        data-testid="application-link-field"
                    />
                    <LoadingButton className={styles.sectionSaveButton} variant="contained" size="small" loading={isSaving} disabled={link === savedLink} onClick={applyChanges} data-testid="save-application-settings-button">
                        Save
                    </LoadingButton>
                </div>
            </FlexColumn>
        );
    }
);

export default PropertyApplicationLinksSection;
